import * as React from 'react'
import { FlatList, View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import IncidentCard from '@components/incidentCard/IncidentCard'
import Button from '@components/button/Button'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import { getIncidentData } from '@screens/incident/utils'
import api from '@api/api'
import Logger from '@utils/logger'

interface Props {
  onBack?: () => void
}

const ExpoHomeScreen = ({ onBack }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)
  const siteInfo = useReducer(SiteStore.store, (s) => s.site!)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('fetched')

  const [cardHeight, setCardHeight] = React.useState(0)

  const [commandes, setCommandes] = React.useState<CommandeListResponse>()

  const location =
    commandes && commandes.commandes.length > 0
      ? `${commandes.commandes[0].enseigne} - ${
          commandes.commandes[0].espace || i18n.t('screens.incident.COMMAND.unknownSpace')
        } - ${commandes.commandes[0].allee || i18n.t('screens.incident.COMMAND.unknownAllee')} - ${
          commandes.commandes[0].stand || i18n.t('screens.incident.COMMAND.unknownStand')
        }`
      : undefined

  const update = () => {
    setStatus('loading')
    getIncidentData(siteInfo, () => setStatus('fetched'))
  }

  React.useEffect(() => {
    update()
  }, [siteInfo.id])

  React.useEffect(() => {
    if (manifestation) {
      api.commande.all(siteInfo.id, manifestation.id).then(setCommandes).catch(Logger.error)
    }
  }, [manifestation])

  const renderIncidentCard = ({ item }: { item: IncidentResponse }) => (
    <IncidentContainer
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}>
      <IncidentCard cardHeight={cardHeight} setCardHeight={assignCardHeight} incident={item} />
    </IncidentContainer>
  )

  const headerLinks = [
    {
      label: i18n.t('screens.home.home'),
      onClick: () => navigation.push('/'),
    },
  ]

  const headerButtons: HeaderButton[] = [
    {
      label: i18n.t('screens.myManifestations.changeManifestation'),
      onClick: () => SiteStore.actions.resetManifestation(),
    },
    {
      label: i18n.t('screens.incidentList.filters.declareIncidentButton'),
      onClick: () => navigation.push('/incident/start'),
    },
  ]

  const renderBottomButton = () => {
    if (isPhone) {
      return (
        <Footer>
          <Button
            label={i18n.t('screens.incidentList.startIncident')}
            onClick={() => navigation.push('/incident/start')}
            status="active"
          />
        </Footer>
      )
    }
  }

  const assignCardHeight = (height: number) => {
    if (height > cardHeight) {
      setCardHeight(height)
    }
  }

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone && manifestation ? manifestation.libelle : undefined}
      fixed={isPhone}
      onLeft={SiteStore.actions.resetManifestation}
      renderFixedBottom={renderBottomButton}>
      <MainContainer>
        {!isPhone && (
          <Header
            // TODO : afficher le nom de la manifestation et les infos complémentaires
            title={`${i18n.t(`screens.site.sites.${siteInfo.codeLieu}`)}${
              manifestation ? ` - ${manifestation.libelle}` : ''
            }`}
            description={location}
            links={headerLinks}
            buttons={headerButtons}
            color="primary"
          />
        )}

        <Count>{i18n.t('screens.incidentList.count', { count: incidents.length })}</Count>

        <FlatList
          data={incidents}
          renderItem={renderIncidentCard}
          contentContainerStyle={{
            flexWrap: 'wrap',
            flexDirection: isPhone ? 'column' : 'row',
            marginTop: 20,
            marginLeft: isPhone ? 0 : 25,
            justifyContent: isPhone ? 'center' : 'flex-start',
          }}
          ListEmptyComponent={<MissingInfo>{i18n.t('screens.incidentList.empty')}</MissingInfo>}
          showsVerticalScrollIndicator={false}
        />
      </MainContainer>
    </Root>
  )
}

export default ExpoHomeScreen

const MainContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && `margin: 18px 16px 0px 16px; flex: 1;`}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const IncidentContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `padding: 4px;` : `margin-right: 8px;`)};
`

const Footer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const Count = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '10px 0px' : '32px 24px 0px 24px')};
`
